import axios from 'axios';

import ApolloAuth from './apolloAuth';
import Session from './session';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'https://api.duelaser.com',
});

// Uma renovação por vez: várias telas do painel disparam requisições em paralelo
// e todas voltariam 401 ao mesmo tempo. Sem isto, cada uma pediria seu próprio
// refresh — e como a Apolo rotaciona o token a cada troca, a segunda chegaria
// com um token já revogado e derrubaria a sessão inteira por suspeita de vazamento.
let refreshPromise = null;

function endSession() {
  Session.clear();
  if (window.location.pathname !== '/') {
    window.location.href = '/';
  }
}

function renewSession() {
  if (refreshPromise) return refreshPromise;

  const refreshToken = Session.getRefreshToken();
  if (!refreshToken) return Promise.reject(new Error('Sessão não encontrada'));

  refreshPromise = ApolloAuth.refresh({ refreshToken })
    .then(data => {
      Session.saveTokens({
        token: data.token,
        refreshToken: data.refreshToken,
      });
      return data.token;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
}

api.interceptors.request.use(async config => {
  if (config.headers.Authorization || config.headers.authorization) {
    return config;
  }

  // O acesso vale 24h e o refresh 30 dias, então o admin continua logado com o
  // cookie de acesso já vencido. Sem renovar aqui, a primeira requisição do dia
  // sairia sem header nenhum e só se recuperaria pelo 401 — o que funciona, mas
  // suja o log da API com "Token não informado" a cada retomada de sessão.
  let token = Session.getToken();
  if (!token && Session.getRefreshToken()) {
    token = await renewSession().catch(() => null);
  }

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

api.interceptors.response.use(
  response => response,
  async error => {
    const original = error.config;

    if (error.response?.status !== 401 || !original) {
      return Promise.reject(error);
    }

    if (original.skipUnauthorizedRedirect) {
      return Promise.reject(error);
    }

    // Já tentamos com um token recém-renovado e continuou 401: não é validade,
    // é a sessão que acabou mesmo.
    if (original.isRetryAfterRefresh) {
      endSession();
      return Promise.reject(error);
    }

    try {
      const token = await renewSession();

      original.isRetryAfterRefresh = true;
      original.headers.Authorization = `Bearer ${token}`;

      return await api(original);
    } catch {
      endSession();
      return Promise.reject(error);
    }
  }
);

export { renewSession };
export default api;
